import { useCallback, useEffect, useMemo, useState } from 'react';
import { transformDataFromHoaDon, transformDataFromHoaDonDichVu } from '../utils/transform-data';
import { PhieuChiInput, type AccountModel, type TaiKhoan } from '@/types/schemas';
import type { PhieuChiFormInitialData, PurchaseInvoiceSource } from '../types';
import { useRouter, useSearchParams } from '@/routing';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';
import api from '@/lib/api';

export function useInvoiceParamsPresence() {
  const searchParams = useSearchParams();
  return useMemo(() => Boolean(searchParams.get('hdmh') || searchParams.get('hdmdv')), [searchParams]);
}

interface UseMultipleFormsArgs {
  addPhieuChi: (data: PhieuChiInput) => Promise<unknown>;
}

export function useMultipleForms({ addPhieuChi }: UseMultipleFormsArgs) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();

  const hasInvoiceParams = useInvoiceParamsPresence();

  const [invoiceData, setInvoiceData] = useState<PurchaseInvoiceSource[]>([]);
  const [showMultipleForms, setShowMultipleForms] = useState<boolean>(hasInvoiceParams);
  const [currentFormIndex, setCurrentFormIndex] = useState<number>(0);

  const taiKhoanSearchData = useMemo(() => ({ prefix: '1111' }), []);
  const { data: taiKhoan, isLoading: isLoadingTaiKhoan } = useCRUD<TaiKhoan, typeof taiKhoanSearchData>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    searchData: taiKhoanSearchData
  });

  // Function to fetch invoice data by UUID
  const fetchInvoiceByUuid = useCallback(
    async (uuid: string, isHoaDonMuaHang: boolean = true): Promise<PurchaseInvoiceSource | null> => {
      if (!entity?.slug) return null;

      try {
        const cacheKey = `hoa_don_detail_${uuid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          sessionStorage.removeItem(cacheKey);
          return JSON.parse(cachedData) as PurchaseInvoiceSource;
        }
        const endpoint = isHoaDonMuaHang ? QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC : QUERY_KEYS.HOA_DON_MUA_DICH_VU;
        const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
        return response.data as PurchaseInvoiceSource;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  useEffect(() => {
    const hdmhParam = searchParams.get('hdmh');
    const hdmdvParam = searchParams.get('hdmdv');
    const editParam = searchParams.get('edit'); // UUID của phiếu chi cần edit

    if (hdmhParam || hdmdvParam) {
      if (editParam) {
        setShowMultipleForms(false);
      } else {
        setShowMultipleForms(true);

        const invoiceUuids = (hdmhParam || hdmdvParam)?.split(',').filter(Boolean) || [];
        const isHoaDonMuaHang = !!hdmhParam;

        if (invoiceUuids.length > 0) {
          Promise.all(invoiceUuids.map(uuid => fetchInvoiceByUuid(uuid, isHoaDonMuaHang)))
            .then(invoices => {
              const validInvoices = invoices.filter((invoice): invoice is PurchaseInvoiceSource => Boolean(invoice));
              setInvoiceData(validInvoices);
              if (validInvoices.length > 0) {
                setCurrentFormIndex(0);
              }
            })
            .catch(() => {
              // Handle error silently, user can still use the form manually
            });
        }
      }
    }
  }, [searchParams, entity?.slug, fetchInvoiceByUuid]);

  // Multiple forms submit handler
  const handleMultipleFormSubmit = useCallback(
    async (data: PhieuChiInput) => {
      try {
        await addPhieuChi(data);
        if (currentFormIndex < invoiceData.length - 1) {
          setShowMultipleForms(true);
          setCurrentFormIndex(currentFormIndex + 1);
        } else {
          setShowMultipleForms(false);
          setInvoiceData([]);
          setCurrentFormIndex(0);
          router.back();
        }
      } catch (error) {
        return;
      }
    },
    [addPhieuChi, currentFormIndex, invoiceData.length, router]
  );

  const handleCloseMultipleForms = useCallback(() => {
    setShowMultipleForms(false);
    setInvoiceData([]);
    setCurrentFormIndex(0);
    router.back();
  }, [router]);

  let initialData: PhieuChiFormInitialData = useMemo(() => {
    const currentInvoice = invoiceData[currentFormIndex];
    if (!currentInvoice) return {};

    const hdmhParam = searchParams.get('hdmh');
    const isHoaDonMuaHang = !!hdmhParam;

    return isHoaDonMuaHang ? transformDataFromHoaDon(currentInvoice) : transformDataFromHoaDonDichVu(currentInvoice);
  }, [invoiceData, currentFormIndex, searchParams]);

  if (!isLoadingTaiKhoan && taiKhoan && taiKhoan.length > 0) {
    initialData = {
      ...initialData,
      tk_data: taiKhoan[0] as unknown as AccountModel
    };
  }

  return {
    hasInvoiceParams,
    showMultipleForms,
    invoiceData,
    currentFormIndex,
    initialData,
    handleMultipleFormSubmit,
    handleCloseMultipleForms
  } as const;
}
